import { Box, Typography, useTheme, Paper } from "@mui/material";
import { GoogleMap, MarkerF, PolylineF } from "@react-google-maps/api";
import { useState } from "react";

import { useIsGoogleMapsApiReady } from "../SharedGoogleMapsProvider";

interface AgencyMapProps {
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
  agencyLatitude?: number | null;
  agencyLongitude?: number | null;
  height?: number;
}

export function AgencyMap({
  facilityLatitude,
  facilityLongitude,
  agencyLatitude,
  agencyLongitude,
  height = 300,
}: AgencyMapProps) {
  const theme = useTheme();
  const isGoogleMapsApiReady = useIsGoogleMapsApiReady();
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Only render if we have both facility and agency coordinates
  if (!facilityLatitude || !facilityLongitude || !agencyLatitude || !agencyLongitude) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={height}
        border="1px solid #ddd"
        borderRadius={1}
        bgcolor="#f5f5f5"
      ></Box>
    );
  }

  const facilityPos = { lat: facilityLatitude, lng: facilityLongitude };
  const agencyPos = { lat: agencyLatitude, lng: agencyLongitude };

  // Calculate center point between facility and agency
  const center = {
    lat: (facilityLatitude + agencyLatitude) / 2,
    lng: (facilityLongitude + agencyLongitude) / 2,
  };
  const fillColor = theme.palette.grey[400];

  // Create facility icon with MUI Factory icon in a circle
  const createFacilityIcon = () => {
    try {
      // Factory icon with gray circle background
      const facilityIconSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="13" fill="${fillColor}" stroke="#999" stroke-width="1"/>
          <g transform="translate(10, 10)">
            <path d="M0 12V4.785L4.2 3V4.2L7.2 3V4.8H12V12H0ZM5.4 9.6H6.6V7.2H5.4V9.6ZM3 9.6H4.2V7.2H3V9.6ZM7.8 9.6H9V7.2H7.8V9.6ZM11.88 3.9H9.105L9.615 0H11.4L11.88 3.9Z" fill="white"/>
          </g>
        </svg>
      `;

      return {
        url: `data:image/svg+xml;base64,${btoa(facilityIconSvg)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 30),
      };
    } catch (error) {
      console.warn("Error creating facility icon:", error);
      // Fallback to circle if icon creation fails
      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor,
        fillOpacity: 1,
        strokeColor: "#999",
        strokeWeight: 2,
      };
    }
  };

  // Dotted line path between facility and agency
  const polylinePath = [facilityPos, agencyPos];

  const polylineOptions = {
    path: polylinePath,
    strokeColor: "transparent",
    strokeOpacity: 0,
    strokeWeight: 1,
    icons: [
      {
        icon: {
          path: "M 0,-1 0,1",
          strokeOpacity: 1,
          strokeWeight: 3,
          strokeColor: "#666666",
        },
        offset: "0",
        repeat: "15px",
      },
    ],
  };

  const handleMapLoad = (map: google.maps.Map) => {
    setIsMapLoaded(true);

    try {
      // Create bounds and include both markers
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(facilityPos);
      bounds.extend(agencyPos);

      // Let Google Maps calculate optimal zoom and center with padding
      map.fitBounds(bounds, 20);
    } catch (error) {
      console.warn("Error applying fitBounds:", error);
      // Fallback to reasonable default zoom
      try {
        map.setZoom(10);
        map.setCenter(center);
      } catch (fallbackError) {
        console.warn("Error in fallback zoom setting:", fallbackError);
      }
    }
  };

  if (!isGoogleMapsApiReady) {
    return (
      <Paper
        sx={{
          height: `${height}px`,
          width: "100%",
          backgroundColor: theme.palette.grey50.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: theme.palette.grey[500],
            fontSize: "0.875rem",
          }}
        >
          Google Maps API is not ready.
        </Typography>
      </Paper>
    );
  }

  return (
    <GoogleMap
      center={center}
      mapContainerStyle={{
        height: `${height}px`,
        width: "100%",
      }}
      mapContainerClassName="rounded-lg"
      options={{
        gestureHandling: "none",
        disableDefaultUI: true,
        zoomControl: false,
        panControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        scrollwheel: false,
        draggable: false,
      }}
      onLoad={handleMapLoad}
    >
      {/* Facility marker with factory icon */}
      <MarkerF position={facilityPos} title="Facility" icon={createFacilityIcon()} />

      {/* Agency marker (default pin) */}
      <MarkerF position={agencyPos} title="Agency" />

      {/* Dotted line between facility and agency */}
      <PolylineF options={polylineOptions} />
    </GoogleMap>
  );
}
